import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Music, Upload, Trash2, Play, Pause, Power, Loader2, Headphones, Square } from "lucide-react";
import ambientMusic from "@/utils/AmbientMusic";

const API = process.env.REACT_APP_BACKEND_URL;
const ROOT = API; // mp3 url is relative (/audio/<file>) but served from backend host

// Display labels for moods. Keys must match the backend ALLOWED_MOODS list.
// Order matters — it's the order the admin sees them in the editor.
const MOOD_LABELS = {
  // Sezioni principali del gioco
  menu: "Home / Menu principale",
  exploration: "Storia (esplorazione)",
  inventory: "Inventario",
  collection: "Collezione",
  shop: "Negozio",
  arena: "Arena (lobby)",
  arena_battle: "PvP (durante il match)",
  grimoire: "Grimorio",
  bestiary: "Bestiario",
  map: "Mappa",
  stats: "Statistiche",
  trophies: "Trofei",
  // Eventi narrativi
  battle: "Battaglia (normale)",
  boss: "Battaglia (Boss)",
  victory: "Vittoria",
  defeat: "Sconfitta",
  chapter_transition: "Transizione capitolo",
  hall: "Hall of Stories",
  character_creation: "Creazione personaggio",
  // Sito vetrina
  landing: "Sito principale (Landing)",
};

const formatSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)}KB`;
  return `${(b / 1024 / 1024).toFixed(2)}MB`;
};

const TrackRow = ({ track, onDelete, onToggle, playingUrl, onPlay }) => {
  const isPlaying = playingUrl === track.url;
  return (
    <div
      data-testid={`audio-track-${track.id}`}
      className={`flex items-center gap-2 px-3 py-2 border border-[#2d3748] rounded-md text-xs transition-colors ${
        track.active === false ? "bg-[#0f0f12] opacity-60" : "bg-[#1a1a1d]"
      }`}
    >
      <button
        type="button"
        onClick={() => onPlay(track.url)}
        className="p-1.5 rounded bg-[#cfb53b]/10 hover:bg-[#cfb53b]/20 text-[#cfb53b]"
        title={isPlaying ? "Pausa" : "Anteprima"}
        data-testid={`audio-preview-${track.id}`}
      >
        {isPlaying ? <Pause size={12} /> : <Play size={12} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-[#e2e8f0] truncate font-medium">{track.original_name}</p>
        <p className="text-[10px] text-[#64748b]">
          {formatSize(track.size)} · caricata {track.uploaded_at?.slice(0, 10)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onToggle(track)}
        className={`p-1.5 rounded transition-colors ${
          track.active === false
            ? "bg-[#475569]/20 text-[#94a3b8] hover:bg-[#475569]/30"
            : "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
        }`}
        title={track.active === false ? "Abilita nella shuffle" : "Disabilita (file resta su disco)"}
        data-testid={`audio-toggle-${track.id}`}
      >
        <Power size={12} />
      </button>
      <button
        type="button"
        onClick={() => onDelete(track)}
        className="p-1.5 rounded bg-red-500/15 text-red-300 hover:bg-red-500/25"
        title="Elimina definitivamente"
        data-testid={`audio-delete-${track.id}`}
      >
        <Trash2 size={12} />
      </button>
    </div>
  );
};

const MoodSection = ({ mood, label, tracks, onUpload, onDelete, onToggle, uploading, playingUrl, onPlay, livePreviewMood, onLivePreview }) => {
  const inputRef = useRef(null);
  const isPreviewActive = livePreviewMood === mood;
  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onUpload(mood, f);
    // Reset so the same filename can be picked again later
    e.target.value = "";
  };
  return (
    <div className="bg-[#1a1a1d]/80 border border-[#2d3748] rounded-lg p-4 space-y-3" data-testid={`audio-mood-${mood}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="font-heading text-sm text-[#cfb53b]">{label}</p>
          <p className="text-[10px] text-[#64748b]">
            {tracks.length === 0
              ? "Nessuna traccia — uso il fallback predefinito"
              : `${tracks.length} tracce nel pool · shuffle attivo`}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onLivePreview(mood)}
            className={`px-2.5 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5 border transition-colors ${
              isPreviewActive
                ? "bg-emerald-500/20 text-emerald-200 border-emerald-400/40 hover:bg-emerald-500/30"
                : "bg-[#0f1a2c] text-blue-300 border-blue-500/30 hover:bg-blue-500/15"
            }`}
            title={isPreviewActive ? "Ferma anteprima live" : "Simula l'ingresso in questo mood (random pick + shuffle)"}
            data-testid={`audio-live-preview-${mood}`}
          >
            {isPreviewActive ? <Square size={12} /> : <Headphones size={12} />}
            {isPreviewActive ? "Stop" : "Live"}
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading === mood}
            className="px-3 py-1.5 bg-[#cfb53b]/15 text-[#cfb53b] border border-[#cfb53b]/30 rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#cfb53b]/25 disabled:opacity-50"
            data-testid={`audio-upload-btn-${mood}`}
          >
            {uploading === mood ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Carica
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/aac,audio/x-m4a,.mp3,.ogg,.wav,.m4a,.aac"
          className="hidden"
          onChange={onPick}
          data-testid={`audio-upload-input-${mood}`}
        />
      </div>
      {tracks.length > 0 && (
        <div className="space-y-1.5">
          {tracks.map((t) => (
            <TrackRow
              key={t.id}
              track={t}
              onDelete={onDelete}
              onToggle={onToggle}
              playingUrl={playingUrl}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default function AudioLibraryTab({ token }) {
  const [tracks, setTracks] = useState([]);
  const [allowedMoods, setAllowedMoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // mood being uploaded
  const [playingUrl, setPlayingUrl] = useState(null);
  // Iter 90.1 — live preview: simulates entering a given mood by routing
  // through the real AmbientMusic engine (so random shuffle + autoplay
  // unlock + volume settings all match what the player actually hears).
  const [livePreviewMood, setLivePreviewMood] = useState(null);
  const previewRef = useRef(null);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/audio/list`, { headers: auth });
      setTracks(r.data?.tracks || []);
      setAllowedMoods(r.data?.allowed_moods || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { refresh(); }, [refresh]);

  const tracksByMood = useMemo(() => {
    const m = {};
    for (const t of tracks) {
      (m[t.mood] = m[t.mood] || []).push(t);
    }
    return m;
  }, [tracks]);

  const onUpload = async (mood, file) => {
    setUploading(mood);
    try {
      const form = new FormData();
      form.append("mood", mood);
      form.append("file", file);
      await axios.post(`${API}/api/admin/audio/upload`, form, {
        headers: { ...auth, "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${file.name}" caricata su ${MOOD_LABELS[mood] || mood}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(null);
    }
  };

  const onDelete = async (track) => {
    if (!window.confirm(`Eliminare definitivamente "${track.original_name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/audio/${track.id}`, { headers: auth });
      toast.success("Eliminata");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Eliminazione fallita");
    }
  };

  const onToggle = async (track) => {
    const next = !(track.active !== false);
    try {
      await axios.patch(
        `${API}/api/admin/audio/${track.id}`,
        { active: next },
        { headers: auth },
      );
      toast.success(next ? "Abilitata" : "Disabilitata dalla shuffle");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modifica fallita");
    }
  };

  const onPlay = (url) => {
    if (playingUrl === url) {
      previewRef.current?.pause();
      setPlayingUrl(null);
      return;
    }
    if (!previewRef.current) previewRef.current = new Audio();
    previewRef.current.src = `${ROOT}${url}`;
    previewRef.current.play().catch(() => toast.error("Riproduzione bloccata dal browser"));
    setPlayingUrl(url);
  };

  // Iter 90.1 — live preview through the real ambient engine. Re-fetches
  // the library first so the admin sees newly-uploaded tracks reflected
  // immediately, then triggers `ambientMusic.play(mood)` which picks a
  // random track from the pool just like the game does in production.
  const onLivePreview = useCallback(async (mood) => {
    if (livePreviewMood === mood) {
      ambientMusic.stop();
      setLivePreviewMood(null);
      return;
    }
    // Stop the single-track preview if one was running
    if (playingUrl) {
      try { previewRef.current?.pause(); } catch { /* noop */ }
      setPlayingUrl(null);
    }
    // Refresh library so just-uploaded tracks are immediately previewable
    await ambientMusic.loadLibrary(API);
    if (!ambientMusic.enabled) {
      ambientMusic.setEnabled(true);
    }
    ambientMusic.play(mood);
    setLivePreviewMood(mood);
  }, [livePreviewMood, playingUrl]);

  // Pause preview when component unmounts
  useEffect(() => () => {
    try { previewRef.current?.pause(); } catch { /* noop */ }
    // Iter 90.1 — stop live preview when navigating away from this tab.
    ambientMusic.stop();
  }, []);

  if (loading && tracks.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-[#94a3b8] text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Caricamento libreria audio…
      </div>
    );
  }

  const moodsToRender = allowedMoods.length
    ? allowedMoods
    : Object.keys(MOOD_LABELS);

  return (
    <div className="space-y-4" data-testid="audio-library-panel">
      <div className="bg-[#1a1a1d]/60 border border-[#cfb53b]/15 rounded-lg p-4 text-xs text-[#94a3b8] flex items-start gap-3">
        <Music className="text-[#cfb53b] shrink-0 mt-0.5" size={16} />
        <div className="flex-1">
          <p>
            Carica i file audio (MP3, OGG, WAV, M4A, AAC — max 15&nbsp;MB) e associali alle varie schermate dell'app.
            Quando un mood ha più di una traccia attiva, il gioco ne sceglie una a caso ad ogni ingresso (shuffle).
            Se un mood non ha tracce, viene usata la traccia predefinita / soundtrack procedurale.
          </p>
          <p className="mt-2 text-[10px] text-[#64748b]">
            <strong className="text-[#cfb53b]/90">Anteprima Live</strong>: clicca "Live" su un mood per simulare l'ingresso del giocatore — la traccia parte attraverso lo stesso motore audio del gioco (shuffle, volume, fade).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {moodsToRender.map((mood) => (
          <MoodSection
            key={mood}
            mood={mood}
            label={MOOD_LABELS[mood] || mood}
            tracks={tracksByMood[mood] || []}
            onUpload={onUpload}
            onDelete={onDelete}
            onToggle={onToggle}
            uploading={uploading}
            playingUrl={playingUrl}
            onPlay={onPlay}
            livePreviewMood={livePreviewMood}
            onLivePreview={onLivePreview}
          />
        ))}
      </div>
    </div>
  );
}
